import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BookOpen, Search, X, Star, Plus, Link as LinkIcon, FileText, StickyNote, Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AddSourceModal from "@/components/sources/AddSourceModal";

const TYPE_ICON = { link: LinkIcon, file: FileText, note: StickyNote };
const POLL_INTERVAL_MS = 7000;

export default function SourceSelector({
  eventId,
  compact = false,
  onSelectionChange,
  draftMode = false,
  selectedIds: controlledSelectedIds,
  primaryId: controlledPrimaryId,
  onSelectedIdsChange,
  onPrimaryIdChange,
}) {
  const navigate = useNavigate();
  const [allSources, setAllSources] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [primaryId, setPrimaryId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingSourceIds, setPendingSourceIds] = useState([]);

  const isDraft = draftMode || !eventId;
  const ids = isDraft && controlledSelectedIds ? controlledSelectedIds : selectedIds;
  const prim = isDraft && controlledPrimaryId !== undefined ? controlledPrimaryId : primaryId;

  const refreshSources = async () => {
    try {
      const resp = await axios.get(`${API}/sources`, { withCredentials: true });
      setAllSources(resp.data.filter(s => s.status === "ready"));
    } catch { /* ignore during poll */ }
  };

  useEffect(() => {
    if (isDraft) {
      (async () => {
        try {
          const resp = await axios.get(`${API}/sources`, { withCredentials: true });
          setAllSources(resp.data.filter(s => s.status === "ready"));
        } catch {
          setAllSources([]);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    (async () => {
      try {
        const [sourcesResp, selResp] = await Promise.all([
          axios.get(`${API}/sources`, { withCredentials: true }),
          axios.get(`${API}/meetings/${eventId}/source-selection`, { withCredentials: true }),
        ]);
        setAllSources(sourcesResp.data.filter(s => s.status === "ready"));
        setSelectedIds(selResp.data?.selected_source_ids || []);
        setPrimaryId(selResp.data?.primary_source_id || null);
      } catch {
        setAllSources([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, isDraft]);

  useEffect(() => {
    if (onSelectionChange) {
      const selected = ids.map(id => allSources.find(s => s.source_id === id)).filter(Boolean);
      onSelectionChange(selected);
    }
  }, [ids, allSources, onSelectionChange]);

  const applyChange = (newIds, newPrimary) => {
    if (isDraft && onSelectedIdsChange) {
      onSelectedIdsChange(newIds);
      if (onPrimaryIdChange) onPrimaryIdChange(newPrimary);
    } else if (isDraft) {
      setSelectedIds(newIds);
      setPrimaryId(newPrimary);
    } else {
      setSelectedIds(newIds);
      setPrimaryId(newPrimary);
      persist(newIds, newPrimary);
    }
  };

  const persist = async (idsToSave, primaryToSave) => {
    if (!eventId || isDraft) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/meetings/${eventId}/source-selection`,
        { selected_source_ids: idsToSave, primary_source_id: primaryToSave },
        { withCredentials: true }
      );
    } catch {
      toast.error("Failed to save source selection");
    } finally {
      setSaving(false);
    }
  };

  const addSource = (sourceId) => {
    if (ids.includes(sourceId)) return;
    const newIds = [...ids, sourceId];
    applyChange(newIds, prim);
    setSearch("");
    setShowSearch(false);
  };

  const removeSource = (sourceId) => {
    const newIds = ids.filter(id => id !== sourceId);
    const newPrimary = prim === sourceId ? null : prim;
    applyChange(newIds, newPrimary);
  };

  const togglePrimary = (sourceId) => {
    const newPrimary = prim === sourceId ? null : sourceId;
    applyChange(ids, newPrimary);
  };

  const autoSelectNewSource = (source) => {
    setAllSources(prev => [source, ...prev]);
    if (source.status === "processing") {
      setPendingSourceIds(prev => [...prev, source.source_id]);
    }
    const newIds = [...ids, source.source_id];
    applyChange(newIds, prim);
  };

  useEffect(() => {
    if (pendingSourceIds.length === 0) return;
    const interval = setInterval(async () => {
      await refreshSources();
      setPendingSourceIds(prev => prev.filter(id => !allSources.some(s => s.source_id === id && s.status === "ready")));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pendingSourceIds, allSources]);

  const _handleSourceError = (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && detail?.error === "source_limit") {
      toast.error(detail.message, {
        action: { label: "Upgrade", onClick: () => navigate("/settings") },
        duration: 8000,
      });
      return;
    }
    toast.error("Failed to add source");
  };

  const handleAddLink = async ({ title, url: linkUrl }) => {
    try {
      const resp = await axios.post(`${API}/sources`, {
        title, source_type: "link", url: linkUrl, content: linkUrl,
      }, { withCredentials: true });
      toast.success("Source added to Knowledge Base");
      autoSelectNewSource(resp.data);
    } catch (err) { _handleSourceError(err); }
  };

  const handleAddNote = async ({ title, content }) => {
    try {
      const resp = await axios.post(`${API}/sources`, {
        title, source_type: "note", content,
      }, { withCredentials: true });
      toast.success("Source added to Knowledge Base");
      autoSelectNewSource(resp.data);
    } catch (err) { _handleSourceError(err); }
  };

  const handleUploadFile = async ({ title, file }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      const resp = await axios.post(`${API}/sources/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File added to Knowledge Base");
      autoSelectNewSource(resp.data);
    } catch (err) { _handleSourceError(err); }
  };

  const selectedSources = ids
    .map(id => allSources.find(s => s.source_id === id))
    .filter(Boolean);

  const filteredSources = allSources.filter(s =>
    !ids.includes(s.source_id) &&
    (s.title.toLowerCase().includes(search.toLowerCase()) ||
     s.topics?.some(t => t.toLowerCase().includes(search.toLowerCase())))
  );

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
      className={compact ? "p-1" : "surface-card rounded-2xl p-6 mb-8"}
    >
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-teal-600" />
            </div>
            <h2 className="font-heading text-foreground text-base">Use sources in this meeting</h2>
            {ids.length > 0 && (
              <Badge variant="outline" className="text-[10px]">{ids.length}</Badge>
            )}
            {saving && <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />}
          </div>
        </div>
      )}
      {compact && saving && (
        <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Saving…
        </div>
      )}

      {/* Selected sources */}
      <AnimatePresence>
        {selectedSources.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedSources.map(source => {
              const Icon = TYPE_ICON[source.source_type] || FileText;
              return (
                <motion.div
                  key={source.source_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`flex items-center gap-1.5 h-8 pl-2.5 pr-1.5 rounded-lg border text-sm transition-colors ${
                    prim === source.source_id
                      ? "border-teal-400 bg-teal-500/5"
                      : "border-border bg-secondary/50"
                  }`}
                >
                  <Icon className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-xs text-foreground truncate max-w-[140px]">{source.title}</span>
                  <button
                    type="button"
                    onClick={() => togglePrimary(source.source_id)}
                    className={`p-0.5 rounded transition-colors ${
                      prim === source.source_id
                        ? "text-teal-500"
                        : "text-muted-foreground/30 hover:text-teal-400"
                    }`}
                    title={prim === source.source_id ? "Primary source" : "Set as primary"}
                  >
                    <Star className="w-3 h-3" fill={prim === source.source_id ? "currentColor" : "none"} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSource(source.source_id)}
                    className="p-0.5 rounded text-muted-foreground/40 hover:text-foreground transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </AnimatePresence>

      {/* Search / add */}
      {showSearch ? (
            <div className="space-y-2">
              <div className="surface-input flex items-center gap-2 h-9 px-3 rounded-lg">
                <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-xs text-foreground outline-none"
                  placeholder="Search sources..."
                  autoFocus
                  onKeyDown={e => { if (e.key === "Escape") { setShowSearch(false); setSearch(""); } }}
                />
                <button type="button" onClick={() => { setShowSearch(false); setSearch(""); }} className="p-0.5">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
              <div className="bg-secondary/50 rounded-lg border border-border max-h-40 overflow-y-auto">
                {filteredSources.slice(0, 8).map(source => {
                  const Icon = TYPE_ICON[source.source_type] || FileText;
                  return (
                    <button
                      type="button"
                      key={source.source_id}
                      onClick={() => addSource(source.source_id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-secondary transition-colors"
                    >
                      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-foreground block truncate">{source.title}</span>
                        {source.short_summary && (
                          <span className="text-[10px] text-muted-foreground block truncate">{source.short_summary}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => { setShowSearch(false); setSearch(""); setShowAddModal(true); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-secondary transition-colors border-t border-border"
                >
                  <Plus className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                  <span className="text-xs text-teal-600 font-medium">Add new source</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Search className="w-3 h-3" />
                {ids.length === 0 ? "Select sources to use in this meeting" : "Add another source"}
              </button>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-500 transition-colors font-medium"
              >
                <Plus className="w-3 h-3" />
                Add new
              </button>
            </div>
          )}

      <AddSourceModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddLink={handleAddLink}
        onAddNote={handleAddNote}
        onUploadFile={handleUploadFile}
      />
    </motion.div>
  );
}
